<template>
    <div>
        <p class="profile-form__subtitle">
            <span class="fas fa-phone"></span>
            {{ $t('Phone') }}
        </p>
        <div class="row">
            <div class="col-md-9 mb-2">
                <ui-phone-input
                ref="phoneInput"
                id="phone"
                v-model="phoneData.phone"
                name="phone"
                type="text"
                placeholder="55 1254 5678"
                :label="$t('The phone number associated with your account')"
                :auto-default-country="!selectedCountry"
                :default-country="selectedCountry"
                :country="selectedCountry"
                :disabled="!isEditing"
                :mode="'update'"
                :field="v$.phoneData.phone"
                @input="handlePhoneData"
                />
            </div>
            <div v-if="!isEditing" class="col-md-1 mb-0 d-flex align-items-center mt-2">
                <ui-button class="mt-2" variant="secondary" @click="changeButton" :disabled="isEditing" >
                    {{ $t('Edit') }}
                    <span class="fas fa-edit"></span>
                </ui-button>
            </div>
            <div v-else-if="isEditing" class="col-md-1 d-flex align-items-center mt-2">
                <ui-button variant="primary" class="change-email-card__save-button" @click="handleSubmit" :disabled="v$.phoneData.$invalid || updatingProfile">
                    <span class="fas fa-check text-success cursor-pointer mx-2"></span>
                </ui-button>
                <ui-button variant="secondary" class="change-email-card__cancel-button"  @click="changeButton">
                    <span class="fas fa-times text-danger cursor-pointer mx-2"></span>
                </ui-button>
            </div>
        </div>
        <vue-recaptcha
        class="w-100"
        ref="phoneRecaptcha"
        @verify="updateProfile"
        size="invisible"
        :sitekey="CONST.CAPTCHACODE"
        :loadRecaptchaScript="true"
        />
    </div>
</template>

<script>
import VueRecaptcha from 'vue-recaptcha';
import UiPhoneInput from '@/components/ui/inputs/Phone.vue';
import UiButton from '@/components/ui/buttons/Button.vue';

import { useVuelidate } from '@vuelidate/core'
import { required, helpers, minLength, maxLength } from '@vuelidate/validators';

import recaptchaUtils from '@/utils/recaptcha.utils';

export default {
    name: 'ChangePhoneForm',
    components: { UiPhoneInput, UiButton, VueRecaptcha },
    props: {
        account: {
            type: Object,
            required: true,
        },
    },
    emits: ['phoneUpdated'],
    setup() {
        return {
            v$: useVuelidate(),
        };
    },
    validations() {
        return {
             phoneData: {
                phone: {
                    required: helpers.withMessage(this.$t('validations.required', { field: this.$t('phone') }), required),
                    minLength: helpers.withMessage(({ $params }) => this.$t('This field must be a minimum of {min} characters', $params ), minLength(8)),
                    maxLength: helpers.withMessage(({ $params }) => this.$t('This field must have a maximum of {max} characters', $params ), maxLength(12)),
                    $autoDirty: true,
                }
            }
        };
    },
    data() {
        return {
           loadingAccount: true,
           updatingProfile: false,
           isEditing: false,
           phoneData: {},
        };
    },
    watch: {
        account: {
            immediate: true,
            handler(account) {
                if (account) {
                    this.phoneData.phone = account.phone;
                }
            }
        }
    },
    mounted() {
        this.getCountries();

        const { phone } = this.account;

        if (phone) {
            this.phoneData.phone = phone;
        }
    },
    computed: {
        selectedCountry() {
            return this.account.phone_country || this.account.country || '';
        }
    },
    methods: {
        async handlePhoneData(value) {
            this.phoneData = { ...this.phoneData, ...value };
        },
        async getCountries() {
            this.countries = await this.fnApiGetCountries();
        },
        changeButton() {
            this.isEditing = !this.isEditing;
            if (!this.isEditing) {
                this.phoneData.phone = this.account.phone;
            }
        },
        handleSubmit() {
            this.v$.phoneData.$touch();

            if (this.v$.phoneData.$invalid) {
                return;
            }

            if (!recaptchaUtils.isAvailable()) {
                this.updateProfile('');
                return;
            }

            this.$refs.phoneRecaptcha.reset();
            this.$refs.phoneRecaptcha.execute();
        },
        async updateProfile(token) {
           try {
                this.updatingProfile = true;

                if (this.phoneData.phone === this.account.phone) {
                    this.changeButton();
                    return;
                }
    
                const payload = {
                    phone: this.phoneData.phone,
                    phone_country: this.phoneData.countryCode,
                    phone_code: this.phoneData.countryCallingCode,
                    token
                };

                const { data } = await this.axiosAccount.put('/api/accounts/me', payload);

                this.$toasted.global.successMessage(this.$t('Phone updated successfully.'));
    
                this.isEditing = false;
                this.$emit('phoneUpdated', data);
                this.loadingAccount = true;
              } catch (error) {
                this.showError(error);
              } finally {
                this.updatingProfile = false;
                this.loadingAccount = false;
                this.isEditing = false;
           }
        }
    }
};
</script>

<style lang="scss">
.change-email-card {
    border: 1px solid #d9d9d9;
    border-radius: 8px;
    padding: 16px;

    &__title {
        color: $general-black;
        margin-bottom: 16px;
    }

    &__content {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 8px;
        width: 100%;
    }

    &__save-button {
        background-color: transparent !important;
        border: none !important;
        display: flex;
        color: $accounts-secondary-600 !important;

        &:hover {
            background-color:transparent !important;
            border: none !important;
            border-color: transparent !important;
        }
    }

    &__cancel-button {
        background-color: transparent !important;
        border: none !important;
        display: flex;
        color: $accounts-secondary-600 !important;

        &:hover {
            background-color:transparent !important;
            border: none !important;
            border-color: none !important;
        }
    }
}
</style>